import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { 
  Upload, Save, FileText, CheckCircle, ArrowLeft, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { API_URL } from '../config/api';

const API = API_URL;

// Helper to strip HTML tags for display in textarea (when editing existing HTML content)
const stripHtmlTags = (html) => {
  if (!html) return '';
  
  // Create a temporary element to parse HTML
  const temp = document.createElement('div');
  temp.innerHTML = html;
  
  // Replace common block elements with newlines
  const blockTags = ['p', 'div', 'br', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li'];
  blockTags.forEach(tag => {
    const elements = temp.getElementsByTagName(tag);
    for (let i = elements.length - 1; i >= 0; i--) {
      const el = elements[i];
      if (tag === 'br') {
        el.replaceWith('\n');
      } else {
        // Add double newline after block elements for paragraph spacing
        el.innerHTML = el.innerHTML + '\n\n';
      }
    }
  });
  
  // Get text content and clean up excess whitespace
  let text = temp.textContent || temp.innerText || '';
  
  // Normalize multiple newlines to max 2 (paragraph break)
  text = text.replace(/\n{3,}/g, '\n\n');
  
  // Trim each line and the overall text
  text = text.split('\n').map(line => line.trim()).join('\n').trim();
  
  return text;
};

// Convert plain text to simple HTML for storage (preserves paragraphs and line breaks)
const textToHtml = (text) => {
  if (!text) return '';
  
  // Split by double newlines for paragraphs
  const paragraphs = text.split(/\n\s*\n/).filter(p => p.trim());
  
  return paragraphs.map(para => {
    // Replace single newlines with <br> within paragraphs
    const lines = para.split('\n').map(line => line.trim()).filter(line => line);
    return `<p>${lines.join('<br>')}</p>`;
  }).join('\n');
};

const AddChapter = () => {
  const { novelId, chapterId } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const [novel, setNovel] = useState(null);
  const [existingChapters, setExistingChapters] = useState([]);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [originalWasHtml, setOriginalWasHtml] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    chapterNumber: 1,
    content: '',
    contentType: 'rich_text',
    authorNote: '',
    isDraft: true
  });

  // Redirect if not authenticated
  useEffect(() => {
    if (!loading && !user) {
      navigate('/auth');
    }
  }, [loading, user, navigate]);

  // Fetch novel and existing chapters
  useEffect(() => {
    if (novelId && user) {
      fetchNovelData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [novelId, user]);

  // Load existing chapter if editing
  useEffect(() => {
    if (chapterId && user) {
      fetchChapterData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterId, user]);

  const fetchNovelData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [novelRes, chaptersRes] = await Promise.all([
        axios.get(`${API}/novels/${novelId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/novels/${novelId}/chapters?include_drafts=true`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setNovel(novelRes.data);
      setExistingChapters(chaptersRes.data);

      if (!chapterId && chaptersRes.data.length > 0) {
        const maxChapter = Math.max(...chaptersRes.data.map(c => c.chapter_number));
        setFormData(prev => ({ ...prev, chapterNumber: maxChapter + 1 }));
      }
    } catch (error) {
      console.error('Error fetching novel:', error);
      toast.error('Failed to load novel data');
    }
  };

  const fetchChapterData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/novel-chapters/${chapterId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const chapter = response.data;
      const rawContent = chapter.content || '';
      
      // Check if content is HTML
      const isHtml = /<[^>]+>/.test(rawContent);
      setOriginalWasHtml(isHtml);
      
      // Convert HTML to plain text for editing
      const displayContent = isHtml ? stripHtmlTags(rawContent) : rawContent;
      
      setFormData({
        title: chapter.title,
        chapterNumber: chapter.chapter_number,
        content: displayContent,
        contentType: chapter.content_type,
        authorNote: chapter.author_note || '',
        isDraft: chapter.is_draft
      });
    } catch (error) {
      console.error('Error fetching chapter:', error);
      if (error.response?.status === 403) {
        toast.error('You can only edit your own chapters');
        navigate(`/novel/${novelId}`);
      } else {
        toast.error('Failed to load chapter');
      }
    }
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const uploadData = new FormData();
      uploadData.append('file', file);

      const response = await axios.post(
        `${API}/novels/${novelId}/chapters/upload-document`,
        uploadData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Convert uploaded HTML to plain text
      const plainText = stripHtmlTags(response.data.html_content);
      setFormData(prev => ({ ...prev, content: plainText }));
      toast.success(`Document uploaded: ${response.data.filename}`);
    } catch (error) {
      console.error('Document upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (publish = false) => {
    if (!formData.title.trim()) {
      toast.error('Chapter title is required');
      return;
    }

    if (!formData.content.trim()) {
      toast.error('Chapter content is required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = chapterId
        ? `${API}/novel-chapters/${chapterId}`
        : `${API}/novels/${novelId}/chapters`;
      const method = chapterId ? 'put' : 'post';

      // Convert plain text to HTML for storage
      const htmlContent = textToHtml(formData.content);

      await axios[method](
        endpoint,
        {
          chapter_number: formData.chapterNumber,
          title: formData.title,
          content_type: 'rich_text', // Keep as rich_text for backward compatibility
          content: htmlContent,
          author_note: formData.authorNote,
          is_draft: !publish,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(publish ? 'Chapter published!' : 'Chapter saved as draft');
      navigate(`/novel/${novelId}`);
    } catch (error) {
      console.error('Error saving chapter:', error);
      if (error.response?.status === 403) {
        toast.error('You can only edit your own chapters');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to save chapter');
      }
    } finally {
      setSaving(false);
    }
  };

  // Word count
  const wordCount = formData.content.trim().split(/\s+/).filter(Boolean).length;
  const charCount = formData.content.length;

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (!novel) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Loading novel...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-16 pb-20" data-testid="add-chapter-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <Button
                variant="ghost"
                onClick={() => navigate(`/novel/${novelId}`)}
                className="text-gray-400 hover:text-white mb-2 -ml-2"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Novel
              </Button>
              <h1 className="text-3xl font-bold text-white mb-2">
                {chapterId ? 'Edit Chapter' : 'Add New Chapter'}
              </h1>
              <p className="text-gray-400">
                Novel: <span className="text-green-400">{novel.title}</span>
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              {lastSaved && (
                <span className="text-sm text-gray-500 flex items-center">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Saved {new Date(lastSaved).toLocaleTimeString()}
                </span>
              )}
              
              <Button
                onClick={() => handleSubmit(false)}
                disabled={saving}
                className="bg-gray-700 hover:bg-gray-600 text-white"
                data-testid="save-draft-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Draft'}
              </Button>
              
              <Button
                onClick={() => handleSubmit(true)}
                disabled={saving}
                className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold"
                data-testid="publish-btn"
              >
                {saving ? 'Publishing...' : 'Publish Chapter'}
              </Button>
            </div>
          </div>
        </div>

        {/* Chapter Metadata */}
        <Card className="bg-gray-900 border-gray-800 mb-6">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-white font-semibold mb-2">
                  Chapter Number
                </label>
                <input
                  type="number"
                  value={formData.chapterNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, chapterNumber: parseInt(e.target.value) || 1 }))}
                  className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                  min="1"
                  data-testid="chapter-number-input"
                />
              </div>

              <div>
                <label className="block text-white font-semibold mb-2">
                  Chapter Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Chapter 1: The Beginning"
                  className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                  data-testid="chapter-title-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Document Upload */}
        <Card className="bg-gray-900 border-gray-800 mb-6">
          <CardContent className="p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2" />
              Upload Document (Optional)
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              Upload a .docx, .pdf, or .txt file to import content into the editor
            </p>
            
            <label className="inline-flex items-center px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg cursor-pointer transition-colors">
              <Upload className="w-5 h-5 mr-2" />
              {uploading ? 'Uploading...' : 'Choose Document'}
              <input
                type="file"
                accept=".docx,.pdf,.txt"
                onChange={handleDocumentUpload}
                className="sr-only"
                disabled={uploading}
              />
            </label>
            <p className="text-xs text-gray-500 mt-2">Maximum file size: 20MB</p>
          </CardContent>
        </Card>

        {/* Simple Text Editor */}
        <Card className="bg-gray-900 border-gray-800 mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold">
                Chapter Content <span className="text-red-500">*</span>
              </h3>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-500">
                  {wordCount} words · {charCount} characters
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPreview(!showPreview)}
                  className="text-gray-400 hover:text-white"
                >
                  {showPreview ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                  {showPreview ? 'Edit' : 'Preview'}
                </Button>
              </div>
            </div>
            
            {originalWasHtml && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4 text-sm text-yellow-400">
                Note: This chapter was originally created with the rich text editor. Formatting has been converted to plain text.
              </div>
            )}
            
            <p className="text-gray-500 text-sm mb-4">
              Write your chapter content below. Use blank lines to separate paragraphs. Newlines within paragraphs are preserved.
            </p>
            
            {showPreview ? (
              /* Preview Mode */
              <div 
                className="bg-gray-800 rounded-lg border border-gray-700 p-6 min-h-[500px] prose prose-invert max-w-none"
                data-testid="chapter-preview"
              >
                {formData.content ? (
                  <div dangerouslySetInnerHTML={{ __html: textToHtml(formData.content) }} />
                ) : (
                  <p className="text-gray-500 italic">No content to preview</p>
                )}
              </div>
            ) : (
              /* Edit Mode */
              <textarea
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Start writing your chapter here...

Use blank lines to create new paragraphs.

Single line breaks will be preserved within paragraphs."
                className="w-full bg-gray-800 text-white px-4 py-4 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 min-h-[500px] resize-y font-mono text-base leading-relaxed"
                data-testid="chapter-content-textarea"
              />
            )}
          </CardContent>
        </Card>

        {/* Author's Note */}
        <Card className="bg-gray-900 border-gray-800 mb-6">
          <CardContent className="p-6">
            <h3 className="text-white font-semibold mb-4">
              Author&apos;s Note (Optional)
            </h3>
            <textarea
              value={formData.authorNote}
              onChange={(e) => setFormData(prev => ({ ...prev, authorNote: e.target.value }))}
              placeholder="Add a note for your readers (this will appear at the end of the chapter)"
              className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 min-h-[120px] resize-y"
              data-testid="author-note-textarea"
            />
          </CardContent>
        </Card>

        {/* Bottom Actions */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => navigate(`/novel/${novelId}`)}
            className="border-gray-700 text-gray-300 hover:bg-gray-800"
          >
            Cancel
          </Button>
          
          <div className="flex items-center space-x-3">
            <Button
              onClick={() => handleSubmit(false)}
              disabled={saving}
              className="bg-gray-700 hover:bg-gray-600 text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Draft'}
            </Button>
            
            <Button
              onClick={() => handleSubmit(true)}
              disabled={saving}
              className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold"
            >
              {saving ? 'Publishing...' : 'Publish Chapter'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddChapter;
